import { useMutation, type UseMutationResult } from '@tanstack/react-query'
import { ApiError } from '../api/client'
import {
  recommendPeakConflict,
  type ConflictRecommendation,
  type RecommendPeakConflictInput,
} from '../api/peak-conflict'

/**
 * Typed error surface for the inline banner. Keeps the call-site free of
 * ad-hoc unknown narrowing — the page just reads `message`.
 */
export interface PeakConflictError {
  message: string
  status: number | null
}

export interface UseManagerPeakConflictResult {
  /** Trigger an explicit recommendation request for a colliding pair. */
  request: (input: RecommendPeakConflictInput) => void
  /** Awaitable variant — same call, returns the payload. */
  requestAsync: (input: RecommendPeakConflictInput) => Promise<ConflictRecommendation>
  /** Latest recommendation payload, or null until a successful call. */
  data: ConflictRecommendation | null
  /** Typed inline-banner error, or null on success/idle. */
  error: PeakConflictError | null
  /** True while the mutation is in-flight. */
  isPending: boolean
  /** Reset to idle (clears data + error). */
  reset: () => void
  /** Escape hatch — the raw mutation, in case a future caller needs it. */
  mutation: UseMutationResult<
    ConflictRecommendation,
    unknown,
    RecommendPeakConflictInput
  >
}

/**
 * Manager-facing hook around `POST /api/v1/manager/peak-conflict/recommend`.
 *
 * Uses `useMutation` (not `useQuery`) on purpose — a peak-conflict
 * recommendation is an explicit user action ("Ask ILMU for a
 * recommendation"), not a background refresh. Caching here would mean
 * stale advice persists past the moment the manager actually decided,
 * which is the wrong default for an advisory surface.
 *
 * The mutation is parameterised at call-time, so a single hook instance
 * can power multiple conflict pairs on the same page (the panel
 * re-renders with the latest `data`).
 *
 * Errors are normalised to `PeakConflictError` so the calling component
 * can render an inline `<Banner>` without re-implementing narrowing for
 * every call-site.
 */
export function useManagerPeakConflict(): UseManagerPeakConflictResult {
  const mutation = useMutation({
    mutationFn: (input: RecommendPeakConflictInput) => recommendPeakConflict(input),
  })

  const error: PeakConflictError | null = mutation.error
    ? normaliseError(mutation.error)
    : null

  return {
    request: (input: RecommendPeakConflictInput) => mutation.mutate(input),
    requestAsync: (input: RecommendPeakConflictInput) => mutation.mutateAsync(input),
    data: mutation.data ?? null,
    error,
    isPending: mutation.isPending,
    reset: () => mutation.reset(),
    mutation,
  }
}

function normaliseError(err: unknown): PeakConflictError {
  if (err instanceof ApiError) {
    return { message: err.message, status: err.status }
  }
  if (err instanceof Error) {
    return { message: err.message, status: null }
  }
  return { message: 'Recommendation failed', status: null }
}
